<!-- modified primevue PanelMenu component -->
<template>
    <div class="panel-menu-height">
        <div class="p-panelmenu p-component">
            <template v-for="(item, index) in model" :key="label(item) + '_' + index">
                <div v-if="visible(item) && item.section" class="sb-sec">
                    <div class="sb-sec-label">{{ label(item) }}</div>
                    <AppPanelMenuSub
                        v-if="item.items"
                        list-class="sb-groups"
                        :model="item.items"
                        :template="$slots.item"
                        :expandedKeys="expandedKeys"
                        @itemToggle="updateExpandedKeys"
                        :exact="exact"
                    />
                </div>
                <div v-else-if="visible(item)" :class="getPanelClass(item)" :style="item.style">
                    <div :class="getHeaderClass(item)" :style="item.style">
                        <template v-if="!$slots.item">
                            <router-link
                                v-if="item.to && !disabled(item)"
                                :to="item.to"
                                custom
                                v-slot="{ navigate, href, isActive, isExactActive }"
                            >
                                <a
                                    :href="href"
                                    :class="getHeaderLinkClass(item, { isActive, isExactActive })"
                                    @click="onItemClick($event, item, navigate)"
                                    role="treeitem"
                                >
                                    <span v-if="item.icon" :class="getPanelIcon(item)"></span>
                                    <span class="p-menuitem-text">{{ label(item) }}</span>
                                </a>
                            </router-link>
                            <a
                                v-else
                                :href="item.url"
                                :class="getHeaderLinkClass(item)"
                                @click="onItemClick($event, item)"
                                :tabindex="disabled(item) ? null : '0'"
                                :aria-expanded="isActive(item)"
                                :id="ariaId + '_header_' + index"
                                :aria-controls="ariaId + '_content_' + index"
                            >
                                <span v-if="item.icon" :class="getPanelIcon(item)"></span>
                                <span class="p-menuitem-text">{{ label(item) }}</span>
                                <svg
                                    v-if="item.items"
                                    class="nav-chevron"
                                    :class="{ 'is-open': isActive(item) }"
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2.5"
                                    aria-hidden="true"
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </a>
                        </template>
                        <component v-else :is="$slots.item" :item="item"></component>
                    </div>
                    <transition name="p-toggleable-content">
                        <div
                            class="p-toggleable-content"
                            v-show="isActive(item)"
                            role="region"
                            :id="ariaId + '_content_' + index"
                            :aria-labelledby="ariaId + '_header_' + index"
                        >
                            <div class="p-panelmenu-content" v-if="item.items">
                                <AppPanelMenuSub
                                    :model="item.items"
                                    class="p-panelmenu-root-submenu"
                                    :template="$slots.item"
                                    :expandedKeys="expandedKeys"
                                    @itemToggle="updateExpandedKeys"
                                    :exact="exact"
                                />
                            </div>
                        </div>
                    </transition>
                </div>
            </template>
        </div>
    </div>
    <div v-if="userConfirmed" class="kam-box">
        <p class="kam-box-text">Your account manager</p>
        <div class="kam-person">
            <Image
                imageClass="kam-image"
                :src="
                    selectedShop !== null &&
                    selectedShop?.sfu_image_url !== '' &&
                    selectedShop?.sfu_image_url !== null
                        ? selectedShop?.sfu_image_url
                        : 'images/avatar/kamAlt.png'
                "
                alt="Account manager"
            />
            <p class="kam-account" style="cursor: text">
                {{ selectedShop?.sfu_full_name }}
            </p>
        </div>
    </div>
</template>

<script>
import AppPanelMenuSub from "./AppPanelMenuSub";
import { UniqueComponentId } from "primevue/utils";
import { useShopStore } from "@/store/shop";
import { useUserStore } from "@/store/user";

export default {
    name: "AppPanelMenu",
    components: {
        AppPanelMenuSub,
    },
    emits: ["update:expandedKeys"],
    props: {
        model: {
            type: Array,
            default: null,
        },
        expandedKeys: {
            type: null,
            default: null,
        },
        exact: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            activeItem: null,
        };
    },
    methods: {
        onItemClick(event, item, navigate) {
            if (this.isActive(item) && this.activeItem === null) {
                this.activeItem = item;
            }

            if (this.disabled(item)) {
                event.preventDefault();
                return;
            }

            if (item.command) {
                item.command({
                    originalEvent: event,
                    item: item,
                });
            }

            if (this.activeItem && this.activeItem === item) {
                this.activeItem = null;
            } else {
                this.activeItem = item;
            }

            if (item.to && navigate) {
                navigate(event);
            }

            this.updateExpandedKeys({ item: item, expanded: this.activeItem !== null });
        },
        updateExpandedKeys(event) {
            if (this.expandedKeys) {
                let item = event.item;
                let _keys = { ...this.expandedKeys };

                if (_keys[item.key]) {
                    delete _keys[item.key];
                } else {
                    _keys[item.key] = true;
                }

                this.$emit("update:expandedKeys", _keys);
            }
        },
        getPanelClass(item) {
            return ["p-panelmenu-panel", item.class, this.isActive(item) ? "active" : ""];
        },
        getPanelToggleIcon(item) {
            const active = this.isActive(item);
            return ["mr-0 ml-auto p-panelmenu-icon pi", { "pi-chevron-down": !active, " pi-chevron-up": active }];
        },
        getPanelIcon(item) {
            return ["p-menuitem-icon", item.icon];
        },
        getHeaderLinkClass(item, routerProps) {
            return [
                "p-panelmenu-header-link",
                {
                    "router-link-active": routerProps && routerProps.isActive,
                    "router-link-active-exact": this.exact && routerProps && routerProps.isExactActive,
                },
            ];
        },
        isActive(item) {
            return this.expandedKeys ? this.expandedKeys[item.key] : item === this.activeItem;
        },
        getHeaderClass(item) {
            return [
                "p-component p-panelmenu-header",
                { "p-highlight": this.isActive(item), "p-disabled": this.disabled(item) },
            ];
        },
        visible(item) {
            return typeof item.visible === "function" ? item.visible() : item.visible !== false;
        },
        disabled(item) {
            return typeof item.disabled === "function" ? item.disabled() : item.disabled;
        },
        label(item) {
            return typeof item.label === "function" ? item.label() : item.label;
        },
    },
    computed: {
        ariaId() {
            return UniqueComponentId();
        },
        selectedShop() {
            return useShopStore().selectedShop;
        },
        userConfirmed() {
            return useUserStore().user.confirmed;
        },
    },
};
</script>

<style>
.p-panelmenu .p-panelmenu-header-link {
    display: flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    position: relative;
    text-decoration: none;
}

.p-panelmenu .p-panelmenu-header-link:focus {
    z-index: 1;
}

.p-panelmenu .p-submenu-list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.p-panelmenu .p-menuitem-link {
    display: flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    text-decoration: none;
}

.p-panelmenu-panel {
    margin-bottom: 10px !important;
}

.kam-box {
    border: 0;
    border-top: 1px solid var(--bm-border);
    border-radius: 0;
    margin: auto 16px 16px;
    outline: none;
    padding: 16px 0 0;
    position: relative;
    user-select: text;
}

.kam-box-text {
    color: var(--bm-grey-400);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.25;
    margin: 0 0 12px;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
}

.kam-person {
    display: flex;
    align-items: center;
    gap: 10px;
}

.kam-account {
    overflow: hidden;
    color: var(--bm-grey-500);
    font-size: 13px;
    font-weight: 400;
    line-height: 1.35;
    margin: 0;
    padding: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.kam-email {
    color: #000;
    font-size: 12px;
    font-weight: 500;
    line-break: anywhere;
    padding-bottom: 3px;
    text-align: center;
}

.panel-menu-height {
    margin-bottom: 10px;
}

.layout-sidebar .panel-menu-height {
    max-height: 100%;
}

.kam-image {
    border-radius: 50%;
    display: block;
    height: 34px;
    margin: 0;
    object-fit: cover;
    width: 34px;
}
</style>
